import { APP_NAME } from '../config';
import { getSyncStatus, onSyncStatus, runSync } from '../data/sync';
import type { SyncStatus } from '../data/sync';
import { syncPill } from '../logic/syncPill';
import { isDesktop, onBreakpointChange } from './breakpoint';
import { linkBtn } from './components';
import { h, span } from './dom';
import { currentPath, navigate } from './router';
import { searchField } from './search';
import { mountSidebar } from './sidebar';
import { loadSidebarCounts } from './sidebarData';

/**
 * App shell: top bar (title + sync pill), view container, bottom tab bar, toast host — and, from
 * 1024 px, a sidebar and a toolbar of large titles instead of the bars (D65).
 * The shell is mounted once; views render into `view`.
 */
export interface Shell {
  view: HTMLElement;
  setTitle: (
    title: string,
    opts?: { back?: boolean | string; subtitle?: string; action?: HTMLElement | null; log?: string },
  ) => void;
  setChrome: (visible: boolean) => void;
  /** `path` lights the phone tab; `navPath` (default `path`) the desktop sidebar item. */
  setActiveTab: (path: string, navPath?: string) => void;
  /** Re-reads the sidebar's counts (desktop only; a no-op on the phone). */
  refreshCounts: () => Promise<void>;
  toast: (
    message: string,
    opts?: { kind?: 'info' | 'error' | 'success'; duration?: number; onClick?: () => void },
  ) => void;
}

const TABS: { href: string; label: string; match: string; primary?: boolean }[] = [
  { href: '#/home', label: 'Home', match: '/home' },
  { href: '#/brief', label: 'Brief', match: '/brief' },
  { href: '#/log', label: 'Log', match: '/log', primary: true },
  { href: '#/route', label: 'Route', match: '/route' },
  { href: '#/more', label: 'More', match: '/more' },
];

/** Where a back link goes, by name, on the desktop toolbar ("‹ Pipeline"). */
const BACK_LABELS: Record<string, string> = {
  '#/home': 'Overview',
  '#/brief': 'Brief',
  '#/activity': 'Activity',
  '#/signals': 'New businesses',
  '#/pipeline': 'Pipeline',
  '#/route': 'Route',
  '#/emails': 'Emails',
  '#/dashboard': 'Reports',
  '#/watchlist': 'Watchlist',
};
/** Screens the sidebar lists: on the desktop their back link is the sidebar itself. */
const SIDEBAR_PATHS = new Set([
  '/home',
  '/brief',
  '/activity',
  '/signals',
  '/pipeline',
  '/route',
  '/emails',
  '/dashboard',
  '/watchlist',
]);

export const mountShell = (root: HTMLElement): Shell => {
  const titleEl = h('h1', { class: 'topbar__title' });
  const subtitleEl = h('div', { class: 'topbar__subtitle mono' });
  const backBtn = h('a', { class: 'topbar__back', href: '#/home', 'aria-label': 'Back', hidden: true }, '‹');
  const actionHost = h('div', { class: 'topbar__action' });
  const pill = h(
    'button',
    {
      class: 'syncpill',
      type: 'button',
      dataset: { state: 'idle' },
      'aria-label': 'Sync status',
      onClick: () => navigate('#/sync'),
    },
    span('syncpill__dot'),
    span('syncpill__text', 'Synced'),
  );
  const topbar = h(
    'header',
    { class: 'topbar' },
    backBtn,
    h('div', { class: 'topbar__titles' }, titleEl, subtitleEl),
    actionHost,
    pill,
  );
  const view = h('main', { class: 'view', id: 'view' });
  const tabbar = h(
    'nav',
    { class: 'tabbar', 'aria-label': 'Primary' },
    TABS.map((t) =>
      h(
        'a',
        {
          class: ['tab', t.primary ? 'tab--primary' : ''].filter(Boolean).join(' '),
          href: t.href,
          dataset: { match: t.match },
        },
        t.primary ? span('tab__plus', '+') : null,
        span('tab__label', t.label),
      ),
    ),
  );
  const toastHost = h('div', { class: 'toasts', 'aria-live': 'polite' });
  const deskBack = h('a', { class: 'desktool__back', href: '#/home', hidden: true });
  const deskTitle = h('h1', { class: 'desktool__title' });
  const deskSubtitle = h('div', { class: 'desktool__subtitle', hidden: true });
  const deskAction = h('div', { class: 'desktool__action' });
  const logCta = linkBtn('+ Log a visit', '#/log', { variant: 'primary', class: 'desktool__cta' });
  const desktool = h(
    'header',
    { class: 'desktool' },
    h('div', { class: 'desktool__titles' }, deskBack, deskTitle, deskSubtitle),
    deskAction,
    logCta,
  );
  const sidebar = mountSidebar(searchField());
  const app = h(
    'div',
    { class: 'app' },
    sidebar.el,
    h('div', { class: 'app__main' }, topbar, desktool, view),
    tabbar,
    toastHost,
  );
  root.replaceChildren(app);

  // the screen's own action lives in the top bar on the phone and in the toolbar on the desktop,
  // and follows the window across the breakpoint without the screen re-rendering
  let action: HTMLElement | null = null;
  const placeAction = () => {
    const [into, out] = isDesktop() ? [deskAction, actionHost] : [actionHost, deskAction];
    out.replaceChildren();
    into.replaceChildren(...(action ? [action] : []));
  };
  onBreakpointChange(placeAction);

  const paintPill = (s: SyncStatus) => {
    const { text, label, state } = syncPill(s);
    pill.dataset.state = state;
    // the accessible name carries the visible words too (a label that differs confuses a screen reader)
    pill.setAttribute(
      'aria-label',
      text === label ? `Sync status: ${text}` : `Sync status: ${text} — ${label}`,
    );
    (pill.querySelector('.syncpill__text') as HTMLElement).textContent = text;
    sidebar.setSync(text, state);
  };
  onSyncStatus(paintPill);
  setInterval(() => paintPill(getSyncStatus()), 60_000); // keep "Synced 3m" honest
  let countsSeq = 0;
  // Tap-and-hold semantics are overkill; a second tap on the pill when idle forces a sync.
  pill.addEventListener('dblclick', () => void runSync());

  const toast: Shell['toast'] = (message, opts = {}) => {
    const dismiss = () => {
      el.classList.remove('toast--in');
      setTimeout(() => el.remove(), 250);
    };
    // An actionable toast fires once and goes away: a second tap would repeat the action
    // (e.g. draft the same follow-up email twice).
    let used = false;
    const onClick = opts.onClick
      ? () => {
          if (used) return;
          used = true;
          dismiss();
          opts.onClick?.();
        }
      : undefined;
    const el = h('div', { class: `toast toast--${opts.kind ?? 'info'}`, role: 'status', onClick }, message);
    // an open dialog dims and silences the page under it: the toasts show inside it meanwhile (D66)
    const host = [...document.querySelectorAll('dialog[open]')].at(-1) ?? app;
    if (toastHost.parentElement !== host) host.appendChild(toastHost);
    toastHost.appendChild(el);
    requestAnimationFrame(() => el.classList.add('toast--in'));
    setTimeout(dismiss, opts.duration ?? (opts.onClick ? 6000 : 2600));
  };

  return {
    view,
    setTitle: (title, opts = {}) => {
      titleEl.textContent = title;
      subtitleEl.textContent = opts.subtitle ?? '';
      subtitleEl.hidden = !opts.subtitle;
      backBtn.hidden = !opts.back;
      const backHref = typeof opts.back === 'string' ? opts.back : '#/home';
      backBtn.setAttribute('href', backHref);
      deskTitle.textContent = title;
      deskSubtitle.textContent = opts.subtitle ?? '';
      deskSubtitle.hidden = !opts.subtitle;
      deskBack.hidden = !opts.back || SIDEBAR_PATHS.has(currentPath());
      deskBack.setAttribute('href', backHref);
      deskBack.textContent = `‹ ${BACK_LABELS[backHref.split('?')[0] ?? ''] ?? 'Back'}`;
      logCta.setAttribute('href', opts.log ?? '#/log');
      action = opts.action ?? null;
      placeAction();
      document.title = title === 'Home' || title === 'Overview' ? APP_NAME : `${title} · ${APP_NAME}`;
    },
    setChrome: (visible) => {
      topbar.hidden = !visible;
      tabbar.hidden = !visible;
      app.classList.toggle('app--bare', !visible);
    },
    setActiveTab: (path, navPath = path) => {
      if (path === '/leads/new') path = '/log';
      for (const a of tabbar.querySelectorAll<HTMLAnchorElement>('.tab')) {
        const m = a.dataset.match ?? '';
        const active =
          path === m ||
          path.startsWith(`${m}/`) ||
          (m === '/more' && !['/home', '/brief', '/log', '/route'].some(
            base => path === base || path.startsWith(`${base}/`)));
        a.classList.toggle('tab--active', active);
        if (active) a.setAttribute('aria-current', 'page');
        else a.removeAttribute('aria-current');
      }
      sidebar.setActive(navPath);
      logCta.classList.toggle('desktool__cta--off', navPath === '/log');
    },
    refreshCounts: async () => {
      if (!isDesktop()) return;
      const mine = ++countsSeq;
      try {
        const counts = await loadSidebarCounts();
        if (mine === countsSeq) sidebar.setCounts(counts); // the latest refresh wins over a slow older one
      } catch {
        // the counts are a nicety: a failed read leaves the last ones up
      }
    },
    toast,
  };
};
